/**
 * MessScan — warden's QR-camera scan page.
 *
 * Uses the BarcodeDetector browser API where available (Chrome / Edge),
 * falls back to a manual paste field. Decoded payload is POSTed to
 * /api/mess/attendance/scan-mark along with the selected meal.
 *
 * Why no react-qr-reader: it adds ~80KB; BarcodeDetector covers 90%+ of
 * Chromium-based browsers used in Indian hostels. Manual paste keeps Safari
 * + Firefox workflows possible via a USB barcode scanner.
 */
import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, CheckCircle, XCircle, QrCode } from "@phosphor-icons/react";
import { toast } from "sonner";

const MEALS = ["breakfast", "lunch", "snacks", "dinner"];

export default function MessScan() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const [meal, setMeal] = useState("breakfast");
  const [camOn, setCamOn] = useState(false);
  const [supported, setSupported] = useState(true);
  const [lastScan, setLastScan] = useState(null);
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("BarcodeDetector" in window)) { setSupported(false); return; }
    try { detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] }); }
    catch (e) { setSupported(false); }
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      setCamOn(true);
      requestAnimationFrame(loop);
    } catch (e) { toast.error(`Camera blocked: ${e.message}`); }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOn(false);
  };

  useEffect(() => { return () => stopCamera(); }, []);

  let lastPayload = "";
  let lastTs = 0;
  const submit = async (payload) => {
    try {
      const r = await api.post("/mess/attendance/scan-mark", { payload, meal });
      setLastScan({ ok: true, ...r.data });
      toast.success(`✓ ${r.data.student_name} · ${meal}`);
    } catch (e) {
      setLastScan({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const loop = async () => {
    if (!camOn || !videoRef.current || !detectorRef.current) return;
    try {
      const codes = await detectorRef.current.detect(videoRef.current);
      const c = codes?.[0];
      if (c?.rawValue && (c.rawValue !== lastPayload || Date.now() - lastTs > 3000)) {
        lastPayload = c.rawValue;
        lastTs = Date.now();
        submit(c.rawValue);
      }
    } catch {}
    if (camOn) requestAnimationFrame(loop);
  };

  // Restart the detector loop whenever camera comes on (keeps state fresh).
  useEffect(() => {
    if (camOn) requestAnimationFrame(loop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camOn]);

  return (
    <DashboardLayout title="Mess QR scan" subtitle="Scan a student's QR card to mark them present for the selected meal. Today's date is used automatically.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="vs-card p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Label>Meal</Label>
              <Select value={meal} onValueChange={setMeal}>
                <SelectTrigger data-testid="scan-meal" className="bg-white capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{MEALS.map((m) => (<SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            {camOn
              ? <Button data-testid="scan-stop" variant="outline" onClick={stopCamera}>Stop camera</Button>
              : <Button data-testid="scan-start" onClick={startCamera} disabled={!supported}><Camera size={14} className="mr-1.5" />Start camera</Button>}
          </div>
          <div className="relative aspect-square bg-black rounded-md overflow-hidden">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            {!camOn && (
              <div className="absolute inset-0 grid place-items-center text-xs text-white/70">
                <QrCode size={48} weight="thin" className="opacity-50" />
              </div>
            )}
          </div>
          {!supported && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              This browser doesn't expose <code>BarcodeDetector</code>. Use Chrome/Edge on Android, or use the manual paste below + USB barcode scanner.
            </div>
          )}
        </div>

        <div className="vs-card p-4 space-y-3">
          <div>
            <Label>Manual paste (USB scanner)</Label>
            <Input data-testid="scan-manual" value={manual} onChange={(e) => setManual(e.target.value)}
              placeholder="VSMS-MESS:…:…" className="bg-white font-mono" />
            <Button size="sm" className="mt-2" data-testid="scan-manual-submit" onClick={() => { submit(manual); setManual(""); }} disabled={!manual.trim()}>Mark present</Button>
          </div>
          <div className="border-t pt-3">
            <div className="eyebrow mb-2">Last scan</div>
            {!lastScan && <div className="text-xs text-[var(--vs-text-secondary)]">No scans yet.</div>}
            {lastScan?.ok && (
              <div className="flex items-center gap-3 text-sm text-emerald-700" data-testid="scan-last-ok">
                <CheckCircle size={20} weight="fill" />
                <div>
                  <div className="font-medium">{lastScan.student_name}</div>
                  <div className="text-xs font-mono">{lastScan.vs_id} · {lastScan.meal} · {lastScan.date}</div>
                </div>
              </div>
            )}
            {lastScan && !lastScan.ok && (
              <div className="flex items-center gap-3 text-sm text-red-700" data-testid="scan-last-fail">
                <XCircle size={20} weight="fill" />
                <div>{lastScan.error}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
